import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import IconButton from 'UIKit/Button/IconButton'
import Image from 'UIKit/Image'
import Text from 'UIKit/Text'
import Toggle from 'UIKit/Toggle/Toggle'
import { Token } from 'config/types'
import { Game, GameDemoSession } from 'config/types/game'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import { useSelectedBalance } from 'layout/Components/BalanceSelect/hooks'
import { RowMiddle } from 'layout/Components/Row'
import { useCallback, useEffect } from 'react'
import { Trans } from 'react-i18next'
import { useAppDispatch, useAppSelector } from 'state'
import { setIsInGame } from 'state/app/actions'
import { useClientIpInfo, useProviderInfo, useResponsiveLayout, useValidatePlayToken } from 'state/app/hooks'
import { useAuth } from 'state/auth/hooks'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { delayed } from 'utils'
import FavoriteGameButton from 'views/Games/components/FavoriteGameButton'
import ShareGameButton from 'views/Games/components/ShareGameButton'
import { useRestrictedGamesC2PBonus } from '../../../hooks/useRestrictedGamesC2PBonus'
import AirdropBoxStorageDesktop from '../AirdropAnimation/AirdropBoxStorageDesktop'
import GameFrame from '../GameFrame'
import GameInfo from '../GameInfo'
import TournamentDetailsOfGame from '../TournamentDetailsOfGame'
import useGameOnboardListener from '../hooks/useGameOnboardListener'
import useGameIframeInfo from './hooks/useGameIframeInfo'
import useIframeUpdater from './hooks/useIframeUpdater'

export interface GameDesktopViewProps {
  game: Game
  gameDemoSession: GameDemoSession
  isMinimize?: boolean
  onClose?: () => void
}

const GameDesktopIframe: React.FC<GameDesktopViewProps> = ({ game, gameDemoSession, isMinimize }) => {
  const { iframeUrl, isLoading, playMode, setPlayMode } = useGameIframeInfo(game, gameDemoSession)
  const { scale, refContentGame, refPrepareGame, iframeContainerRef, toggleFullScreen } = useIframeUpdater(iframeUrl)

  const { isSigned, hasSession } = useAuth()
  const dispatch = useAppDispatch()
  const responsive = useResponsiveLayout()

  const { selectedBalance, handleSelectBalance } = useSelectedBalance()
  const provider = useProviderInfo(game.providerCode)
  const { country } = useClientIpInfo()
  const { validate } = useValidatePlayToken(selectedBalance.token)
  const isBlockedCountry = provider?.blacklistCountry?.includes(country)
  const isIngame = useAppSelector((state) => state.app.isInGame)
  const isRestrictedGame = useRestrictedGamesC2PBonus(game.code)
  const { isTablet, isDesktop } = useMatchBreakpoints()

  useGameOnboardListener(() => {
    dispatch(setIsInGame(true))
  }, game)

  const handleToggleModeGame = useCallback(
    async (isDemo?: boolean, selectedToken?: Token) => {
      setPlayMode(isDemo ? 'demo' : 'real')

      if (isBlockedCountry || isRestrictedGame || game.isMaintenance) return

      const playToken = (selectedToken?.code && selectedToken) || selectedBalance.token
      if (!hasSession || !validate(playToken)) {
        if (isDemo) {
          dispatch(setIsInGame(true))
        }
        return
      }

      handleSelectBalance({ token: playToken } as any)
      await delayed(200)
      dispatch(setIsInGame(true))
    },
    [selectedBalance.token, hasSession, isIngame, isBlockedCountry, isRestrictedGame, game.isMaintenance],
  )

  useEffect(() => {
    if (isRestrictedGame) {
      dispatch(setIsInGame(false))
    }
  }, [isRestrictedGame])

  return (
    <>
      <StyledContainer
        game={game}
        isMinimize={isMinimize}
        margin="auto"
        background={isMinimize ? 'rgba(35, 44, 61, 0) !important' : ''}
        overflow="visible !important"
      >
        {!isMinimize && (
          <>
            <Flex
              height={responsive(['78px', '78px', '65px'])}
              mt={responsive(['6px', '6px', '0px'])}
              position="absolute"
              bottom="0px"
              right="20px"
              left="20px"
              justifyContent="space-between"
              alignItems="center"
              flexWrap={responsive(['wrap', 'wrap', 'nowrap'])}
              zIndex={5}
            >
              <RowMiddle
                minWidth={responsive(['100%', '100%', '200px'])}
                justifyContent={responsive(['space-between', 'space-between', 'start'])}
              >
                <RowMiddle>
                  <RowMiddle>
                    <FavoriteGameButton game={game} mr="16px" size="28px" />
                    <ShareGameButton game={game} mr="16px" size="28px" />
                  </RowMiddle>

                  <StyledFullscreenButton
                    onClick={game.isMaintenance || isBlockedCountry || isRestrictedGame ? () => {} : toggleFullScreen}
                    $isDisabled={game.isMaintenance || isBlockedCountry || isRestrictedGame}
                  >
                    <Icons.FullScreenIcon width="28px" height="28px" />
                  </StyledFullscreenButton>

                  {gameDemoSession && (
                    <Flex alignItems="center" ml="16px">
                      <Toggle
                        defaultColor="textDisable"
                        checkedColor="textTertiary"
                        backgroundColor="modalHeaderBackground"
                        checkedBackgroundColor="success"
                        value={playMode === 'demo'}
                        onValueChanged={handleToggleModeGame}
                      />
                      <Text bold small mx="5px" color={playMode === 'demo' ? '#fff' : 'textSubtle'}>
                        <Trans>Demo mode</Trans>
                      </Text>
                    </Flex>
                  )}

                  {game.rtp && (
                    <>
                      <Box width="5px" height="5px" background="#fff" borderRadius="50%" mx="12px" ml="28px" />
                      <Text fontSize="20px" fontWeight={700}>
                        RTP {game.rtp}
                      </Text>
                    </>
                  )}
                </RowMiddle>
              </RowMiddle>

              <Flex>
                {isDesktop && <TournamentDetailsOfGame height="49px" />}
                {isSigned && <StyledAirdropBox ml="20px" width="fit-content !important" />}
              </Flex>
            </Flex>
          </>
        )}

        <StyledGameContent ref={refContentGame} position="relative" background="black">
          {!iframeUrl && (
            <StyledImageBackground
              position="absolute"
              top="50%"
              left="50%"
              width="100%"
              minWidth="900px"
              overflow="hidden"
            >
              <Image src={game.thumbnail} width={1920} height={1920} blurLoader />
            </StyledImageBackground>
          )}

          <Box
            ref={iframeContainerRef}
            position="absolute"
            width="100%"
            height="100%"
            style={{
              filter: isMinimize || (isIngame && !isLoading) ? '' : 'blur(7px)',
              display: iframeUrl ? 'block' : 'none',
            }}
          />

          <StyledPrepareGameBackground
            position="absolute"
            height={isIngame ? '0%' : '100%'}
            width="100%"
            overflow="hidden"
            background="rgba(22, 25, 31, 0.7)"
          >
            <GameInfo
              width="100%"
              height="100%"
              handlePlayGame={handleToggleModeGame}
              game={game}
              gameDemoSession={gameDemoSession}
              ref={refPrepareGame}
              scale={scale}
            />
          </StyledPrepareGameBackground>
        </StyledGameContent>
      </StyledContainer>
      {isTablet && <TournamentDetailsOfGame mt="12px !important" width="100%" />}
    </>
  )
}

const StyledAirdropBox = styled(AirdropBoxStorageDesktop)`
  height: 100%;
  justify-content: center !important;
  .warpper-box {
    width: 36px;
  }

  .wrapper-container {
    width: fit-content;
  }
  #airdrop-gift-box {
    width: 36px !important;
    height: 36px !important;
    margin-right: 0px;

    .box-storage {
      height: 100%;

      .airdrop-box-storage {
        width: 100%;
        height: 100%;

        div {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
        }
        img {
          width: 100% !important;
          height: 100% !important;
          max-width: 100% !important;
          max-height: 100% !important;
        }
      }
    }
  }
`

const StyledImageBackground = styled(Box)`
  transform: translate(-50%, -50%);
  filter: blur(7px);
`

const StyledPrepareGameBackground = styled(Box)`
  transition: 1s;
`

const StyledGameContent = styled(Box)`
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const StyledContainer = styled(GameFrame)`
  margin: auto;
  border-radius: ${({ theme }) => theme.radii.small};
  overflow: hidden;
  display: flex;
  position: relative;
  flex-direction: column;
  background: rgba(35, 44, 61, 0.2);
`

const StyledFullscreenButton = styled(IconButton)<{ $isDisabled: boolean }>`
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'not-allowed !important' : 'pointer')};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.3 : 1)};

  &:hover {
    svg {
      fill: ${({ theme, $isDisabled }) => (!$isDisabled ? theme.colors.text : theme.colors.textSubtle)} !important;
    }
  }
`
export default GameDesktopIframe
